<template>
  <div class="py-3 container-fluid space-y-4">

    <!-- Header usuario y fechas -->
    <div class="bg-white rounded-xl shadow-md p-3">
      <div class="flex flex-col md:flex-row md:items-center gap-3">
        <!-- Usuario -->
        <div class="flex items-center gap-3 w-full">
          <img :src="acreditado && acreditado.foto ? acreditado.foto : require('@/assets/img/nologo.png')" class="shadow-sm w-16 rounded-lg" />
          <div class="flex flex-col">
            <h5 class="text-xl font-bold mb-1 text-slate-800">{{ acreditado ? acreditado.nombre + " " + acreditado.apellido : $t('label.cargando') }}</h5>
            <p class="mb-0 text-xs text-slate-500">Fecha de alta : {{ acreditado ? formatDate(acreditado.fechaAlta, 'dd/MM/yyyy HH:mm:ss') : $t('label.cargando') }}</p>
          </div>
        </div>        
      </div>
    </div>
    <!-- Fin header usuario y fechas -->

    <!-- Listado de bloqueos -->
    <div class="bg-white rounded-xl shadow-md">
      
      <div class="p-3 mb-2 space-y-4">
        <h6 class="text-lg font-bold text-slate-800">{{ $t('menu.bloquear.horario')}}</h6>
        <NavPill :items="fechas" v-model="fechaSeleccionada" :onChange="onChangeFechaCronograma" />
      </div>

      <div v-if="!isSubmitting && detalleAgenda.length == 0" class="py-4 text-center text-lg md:text-2xl mx-auto">
        {{ $t('label.sin.restricciones.para')}}{{ fechaSeleccionada }}
      </div>

      <ul class="divide-y divide-gray-200 list-none pb-4">
        <li v-for="(item, i) in detalleAgenda" :key="i" @click="desbloquear(item.idAgenda)" class="hover:bg-gradient-to-r from-gray-200 to-gray-50 cursor-pointer" v-tooltip.top="'Eliminar bloqueo'" >
          <div class="flex items-center gap-3 py-2 mx-3">
            <div class="h-14 w-14 rounded-lg shadow flex items-center justify-center border" :style="{ borderColor: item.colorEstado, color: item.colorEstado }">
              <font-awesome-icon :icon="['fas', 'fa-lock']" class="text-3xl" />
            </div>

            <div class="flex-1 text-left ">
              <div class="text-sm md:text-lg font-semibold">{{ item.cronograma.horaDesde }}</div>
              <div class="text-sm text-gray-500 line-clamp-2">{{ item.observaciones }}</div>
            </div>

            <div class="relative">
              <button class="p-2 text-gray-500">
                <font-awesome-icon :icon="['fas', 'fa-unlock']" class="text-2xl mr-2 text-gray-500" />
              </button>
            </div>
          </div>
        </li>
      </ul>
    </div>
    <!-- Fin Agenda -->

    <div class="w-full flex flex-col md:flex-row md:w-auto gap-2">
      <button @click="mostrarNuevoBloqueo" class="w-full md:w-auto text-sm px-5 font-bold text-white py-3 bg-gradient-to-r from-[#424c42] to-[#bbc1a9] shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
        <font-awesome-icon :icon="['fas', 'fa-lock']" class="mr-2" />
        {{ $t('boton.cargar.bloqueo') }}
      </button>
      <button @click="bloquearDiaCompleto" class="w-full md:w-auto text-sm px-5 font-bold text-white py-3 bg-gradient-to-r from-[#424c42] to-[#bbc1a9] shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
        <font-awesome-icon :icon="['fas', 'fa-calendar-xmark']" class="mr-2" />
        {{ $t('boton.bloqueo.dia') }}
      </button>
    </div>

  </div>

  <NuevoBloqueoView v-if="nuevoBloqueoVisible" :closeFunction="closeDialogBloqueo" :fechaBloqueo="fechaSeleccionada"/>
  <FullScreenLoader :visible="isSubmitting" />

</template>

<script lang="ts">
import { defineComponent } from "vue";
import Swal from 'sweetalert2'

import NavPill from "@/components/commons/NavPill.vue";
import FullScreenLoader from "@/components/commons/FullScreenLoader.vue";

import { Acreditado, Evento } from "qreventos-suite/services";
import { formatDate } from '@/utils/formatters';

import seguridadServices from "@/services/seguridad.services";
import meetingService, { ConfiguracionMeeting } from 'qreventos-suite/meeting';
import configuracionMeetingService from '@/services/configuracion.merting.services';
import { DetalleAgenda } from "qreventos-suite/src/services/suite.interfaces";
import NuevoBloqueoView from "./modal/NuevoBloqueoView.vue";

export default defineComponent({
  components: {
    NavPill, FullScreenLoader,
    NuevoBloqueoView
  },
  data() {
    return {
      isSubmitting: false,
      fechas: [] as string[],
      fechaSeleccionada: '',
      detalleAgenda: [] as DetalleAgenda[],
      acreditado: undefined as unknown as Acreditado,
      nuevoBloqueoVisible: false,
    };
  },
  methods: {
    formatDate,
    async fillAcreditado(evento:Evento, hash:string) {
      this.acreditado = await meetingService.findAcreditadoMeetingBy(evento.id, hash);
    },
    async fillFechasBy(evento: Evento) {
      this.fechas = await meetingService.findFechasBy(evento.id);
      this.fechaSeleccionada = this.fechas[0];
    },
    async fillDetallesBloqueos() {
      const hash: string = seguridadServices.getLocalHash() as string;
      const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;
      this.detalleAgenda = await meetingService.findDetalleAgendaFechaBy(configuracionMeeting.evento.id, hash, 2, this.fechaSeleccionada);
    },
    async onChangeFechaCronograma(fecha: string) {
      try {
        this.fechaSeleccionada = fecha;        

        this.isSubmitting = true;
        await this.fillDetallesBloqueos();
      } catch (error) {
        this.isSubmitting = false;
        Swal.fire({ title: "Error", text: error + '', icon: "error" });
      } finally {
        this.isSubmitting = false;
      }
    },
    /** Gestión de desbloqueo */
    async desbloquear(idAgenda:number) {
      Swal.fire({
        title: this.$t('global.atencion'),
        html: this.$t('label.pregunta.liberar.horario'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: this.$t('boton.liberar'),
        cancelButtonText: this.$t('boton.cancelar'),
        buttonsStyling: true
      }).then((isConfirm) => {
        if (isConfirm.value === true) {
          this.liberarBloqueo(idAgenda);
        }
      });
    },
    async liberarBloqueo(idAgenda:number) {
      try {
        const hash: string = seguridadServices.getLocalHash() as string;
        const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

        const liberado:boolean = await meetingService.desbloquear(configuracionMeeting.evento.id, hash, idAgenda);
        if(liberado) {
          Swal.fire({ title: this.$t('global.atencion'), html: this.$t('label.horario.liberado'), icon: 'info' }).then(() => {
            this.fillDetallesBloqueos();
          });
        }
      }
      catch (error) {
        Swal.fire({ title: "Error", text: "" + error + "", icon: "error" }).then(() => {
          this.fillDetallesBloqueos();
        });
      }  
    },    
    /** Gestión de modal de nuevo bloqueo */
    mostrarNuevoBloqueo() {
      this.nuevoBloqueoVisible = true;
    },
    bloquearDiaCompleto() {
      Swal.fire({
        title: this.$t('global.atencion'),
        html: this.$t('label.pregunta.bloquear.dia.completo'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('boton.si.bloqueo.dia'),
        cancelButtonText: this.$t('boton.cancelar'),
        buttonsStyling: true
      }).then((isConfirm) => {
        if (isConfirm.value === true) {  
          this.confirmacionBloquearDiaCompleto();
        }
      });
    },
    async confirmacionBloquearDiaCompleto() {
      try {
        this.isSubmitting = true;
        const hash: string = seguridadServices.getLocalHash() as string;
        const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

        await meetingService.bloquearDiaCompleto(configuracionMeeting.evento.id, hash, this.fechaSeleccionada);
        Swal.fire({ title: this.$t('global.atencion'), html: this.$t('label.dia.bloqueado'), icon: 'success' }).then(() => {
          this.fillDetallesBloqueos();
        });
      }
      catch (error) {
        this.isSubmitting = false;
        Swal.fire({ title: "Error", text: "" + error + "", icon: "error" }).then(() => {
          this.fillDetallesBloqueos();
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    closeDialogBloqueo(reload: boolean = false) {
      this.nuevoBloqueoVisible = false;
      if (reload) {
        try {
          this.fillDetallesBloqueos();
        } catch (error) {
          this.isSubmitting = false;
          Swal.fire({ title: "Error", text: error + '', icon: "error" });
        } finally {
          this.isSubmitting = false;
        }
      }
    },
    /** Fin gestión de modal de nuevo bloqueo */
  },
  async mounted() {
    try {
      this.isSubmitting = true;
      const hash: string = seguridadServices.getLocalHash() as string;
      const configuracionMeeting: ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

      await this.fillFechasBy(configuracionMeeting.evento);
      await this.fillAcreditado(configuracionMeeting.evento, hash);
      await this.fillDetallesBloqueos();

    } catch (error) {
      this.isSubmitting = false;
      Swal.fire({ title: "Error", text: error + '', icon: "error" });
    } finally {
      this.isSubmitting = false;
    }
  }
});
</script>